






















































































































import {
  defineComponent,
  PropType,
  computed,
  watch,
  ref,
  onMounted,
} from '@vue/composition-api';
import {
  AppliedProperty,
  ItemPropertyRow,
  PreselectionProperty,
  PropertyValue,
  PropertyVariable,
} from '@oare/types';
import sl from '@/serviceLocator';
import PropertyInfo from './PropertyInfo.vue';

export default defineComponent({
  name: 'PropertyValue',
  props: {
    value: {
      type: Object as PropType<PropertyValue>,
      required: true,
    },
    parentVariable: {
      type: Object as PropType<PropertyVariable>,
      required: true,
    },
    showUuid: {
      type: Boolean,
      default: false,
    },
    nodesToHighlight: {
      type: Array as PropType<String[]>,
      default: [],
    },
    openSearchResults: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      required: true,
    },
    overrideCustom: {
      type: Boolean,
      default: false,
    },
    showValidation: {
      type: Boolean,
      default: true,
    },
    existingProperties: {
      type: Array as PropType<ItemPropertyRow[]>,
      required: false,
    },
    valuesToPreselect: {
      type: Array as PropType<PreselectionProperty[]>,
      required: false,
    },
  },
  components: {
    PropertyInfo,
  },
  setup(props, { emit }) {
    // To avoid circular dependencies
    const propertyVariableComponent = computed(
      () => () => import('./PropertyVariableDisplay.vue')
    );

    const actions = sl.get('globalActions');

    const copyUuid = (uuid: string) => {
      navigator.clipboard.writeText(uuid);
      actions.showSnackbar('Copied Successfully');
    };

    const openPanels = ref<number>();

    watch(
      () => props.openSearchResults,
      () => {
        if (props.openSearchResults) {
          props.value.variables.forEach((variable, idx) => {
            if (
              props.nodesToHighlight.includes(variable.hierarchy.uuid) &&
              variable.values.length > 0
            ) {
              openPanels.value = idx;
            }
          });
        } else {
          openPanels.value = undefined;
        }
      },
      { immediate: true }
    );

    const selected = ref(false);
    watch(selected, isSelected => {
      if (isSelected) {
        appliedProperties.value.push({
          variableRow: props.parentVariable,
          valueRow: props.value,
          objectUuid: null,
          objectDisplay: null,
          value: null,
          sourceUuid: props.value.hierarchy.uuid,
        });
      } else {
        appliedProperties.value = appliedProperties.value.filter(
          p =>
            p.sourceUuid !== props.value.hierarchy.uuid ||
            !p.valueRow ||
            p.valueRow.hierarchy.uuid !== props.value.hierarchy.uuid
        );
      }
    });

    const appliedProperties = ref<AppliedProperty[]>([]);
    watch(appliedProperties, () => {
      emit(
        'update-properties',
        appliedProperties.value.map(p => ({
          ...p,
          sourceUuid: props.value.hierarchy.uuid,
        }))
      );
    });
    const updateProperties = (
      childProperties: AppliedProperty[],
      variable: PropertyVariable
    ) => {
      appliedProperties.value = appliedProperties.value.filter(
        p => p.sourceUuid !== variable.hierarchy.uuid
      );
      appliedProperties.value.push(...childProperties);
      // Automatically select property if a child has been selected
      if (childProperties.length > 0 && !selected.value) {
        selected.value = true;
      }
    };

    // If there are already properties below the value that are selected, then you cannot unselect the current value
    const selectIsDisabled = computed(() => {
      if (selected.value) {
        return appliedProperties.value.length > 1;
      } else {
        return appliedProperties.value.length > 0;
      }
    });

    const autoOpenNext = (idx: number) => {
      openPanels.value = undefined;
      if (idx + 1 < props.value.variables.length) {
        openPanels.value = idx + 1;
      }
    };

    const prepareValueSelected = () => {
      if (props.value.variables.length === 0) {
        emit('value-selected');
      }
    };

    const showCheck = computed(() => {
      if (ignored.value) {
        return true;
      }

      const numSubtrees = props.value.variables.filter(
        variable => variable.values.length > 0
      ).length;
      const numSubvariablesWithoutSubtrees = props.value.variables.filter(
        variable => variable.values.length === 0 && variable.type !== 'serial'
      ).length;
      return (
        numSubtrees + numSubvariablesWithoutSubtrees ===
        completedSubtrees.value.length
      );
    });
    const ignored = ref(false);

    const completedSubtrees = ref<string[]>([]);

    watch(showCheck, isComplete => {
      emit('completed', isComplete);
    });
    watch(ignored, isIgnored => {
      emit('ignored', isIgnored);
    });

    const handleCompleted = (uuid: string, isCompleted: boolean) => {
      if (isCompleted) {
        completedSubtrees.value.push(uuid);
      } else {
        completedSubtrees.value = completedSubtrees.value.filter(
          u => u !== uuid
        );
      }
    };
    const handleIgnored = (idx: number, isIgnored: boolean) => {
      if (isIgnored && idx === openPanels.value) {
        openPanels.value = undefined;
      }
    };

    const variablesToDisplay = computed(() => {
      return props.value.variables;
    });

    onMounted(() => {
      // If existing properties are supplied, they will be automatically selected on mount
      if (props.existingProperties) {
        const relevantProperties = props.existingProperties.filter(p => {
          const parentProperty = props.existingProperties!.find(
            exis => exis.uuid === p.parentUuid
          );
          const hasValidParentRelationship = parentProperty
            ? props.parentVariable.hierarchy.objectParentUuid ===
              parentProperty.valueUuid
            : true;

          const hasValidGrandparentRelationship = parentProperty
            ? props.parentVariable.hierarchy.objectGrandparentUuid ===
              parentProperty.variableUuid
            : true;
          return (
            p.level === props.parentVariable.level &&
            p.variableUuid === props.parentVariable.uuid &&
            p.valueUuid === props.value.uuid &&
            hasValidParentRelationship &&
            hasValidGrandparentRelationship
          );
        });
        if (relevantProperties.length !== 1) {
          return;
        }
        selected.value = true;
      }

      // If values are to be preselected (not the same as starting points or existing properties), then they will be selected here
      if (props.valuesToPreselect) {
        const valuesWithRelevantVariable = props.valuesToPreselect.filter(
          p => p.variableHierarchyUuid === props.parentVariable.hierarchy.uuid
        );
        if (
          valuesWithRelevantVariable
            .map(p => p.valueUuid)
            .includes(props.value.uuid)
        ) {
          selected.value = true;
        }
      }
    });

    return {
      propertyVariableComponent,
      copyUuid,
      openPanels,
      selected,
      updateProperties,
      selectIsDisabled,
      autoOpenNext,
      prepareValueSelected,
      showCheck,
      completedSubtrees,
      ignored,
      handleCompleted,
      appliedProperties,
      handleIgnored,
      variablesToDisplay,
    };
  },
});
